import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Dog,
  Check,
  AlertCircle,
  ChevronRight,
  CreditCard,
  Building2,
  Copy,
  CheckCircle
} from "lucide-react";
import { trackEnrollmentStart, trackPurchase } from "@/utils/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFERRAL_SOURCES = [
  { value: "google", label: "Google Search" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "friend_family", label: "Friend or Family Referral" },
  { value: "breeder", label: "My Breeder" },
  { value: "vet", label: "Vet Recommendation" },
  { value: "pet_store", label: "Pet Store" },
  { value: "local_event", label: "Local Event" },
  { value: "other", label: "Other" }
];

const EnrollmentPage = () => {
  const { intakeId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [intake, setIntake] = useState(null);
  const [puppies, setPuppies] = useState([]);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  
  const [selectedPuppy, setSelectedPuppy] = useState(null);
  const [referralSource, setReferralSource] = useState("");
  const [referralDetails, setReferralDetails] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [bankTransferSubmitted, setBankTransferSubmitted] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [intakeRes, puppiesRes, paypalRes, methodsRes] = await Promise.all([
        axios.get(`${API}/intakes/${intakeId}`),
        axios.get(`${API}/puppies`, { headers }),
        axios.get(`${API}/paypal-client-id`),
        axios.get(`${API}/payment-methods`)
      ]);
      setIntake(intakeRes.data);
      setPuppies(puppiesRes.data);
      setPaypalClientId(paypalRes.data.client_id);
      setPaymentMethods(methodsRes.data);
    } catch (err) {
      toast.error("Failed to load enrollment data");
    } finally {
      setLoading(false);
    }
  }, [intakeId, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Show all puppies - age restriction removed to allow advance bookings
  // Puppies will be the right age by the time the course starts
  const eligiblePuppies = puppies;

  // Calculate puppy's age at course start date
  const calculateAgeAtCourseStart = (puppy) => {
    if (!intake || !puppy.date_of_birth) return null;
    const dob = new Date(puppy.date_of_birth);
    const courseStart = new Date(intake.start_date);
    const diffTime = courseStart - dob;
    const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
    return diffWeeks;
  };

  const createEnrollment = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/enrollments`, {
        puppy_id: selectedPuppy.id,
        intake_id: intakeId,
        referral_source: referralSource,
        referral_details: referralDetails
      }, { headers });
      setEnrollment(response.data);
      setStep(2);
      // Track begin checkout conversion
      trackEnrollmentStart(intake?.program_name, intakeId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create enrollment");
    }
  };

  const handlePaymentSuccess = async (details) => {
    setProcessingPayment(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `${API}/enrollments/${enrollment.id}/payment?paypal_order_id=${details.id}`,
        {},
        { headers }
      );
      // Track purchase conversion
      trackPurchase(440, 'AUD', details.id);
      toast.success("Payment successful! Your puppy is enrolled.");
      // Navigate to thank you page with enrollment data
      navigate("/thank-you", { 
        state: { 
          enrollment: {
            puppy_name: selectedPuppy?.name,
            program_name: intake?.program_name
          }
        }
      });
    } catch (error) {
      toast.error("Payment recorded but confirmation failed. Please contact us.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStripeCheckout = async () => {
    setProcessingPayment(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/stripe/create-checkout`, {
        enrollment_id: enrollment.id,
        origin_url: window.location.origin
      }, { headers });
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setProcessingPayment(false);
    }
  };

  const handleBankTransfer = async () => {
    setProcessingPayment(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/enrollments/${enrollment.id}/bank-transfer`, {}, { headers });
      setBankDetails(res.data.bank_details);
      setBankTransferSubmitted(true);
      toast.success("Bank transfer details sent to your email!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process request");
    } finally {
      setProcessingPayment(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  if (!intake || intake.available_spots <= 0) {
    return (
      <div className="min-h-screen bg-off-white" data-testid="enrollment-page">
        <header className="bg-white border-b border-olive/10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/programs" className="inline-flex items-center text-stone hover:text-olive">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Programs
            </Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <AlertCircle className="w-16 h-16 text-terracotta mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-charcoal mb-4">Program Unavailable</h1>
          <p className="text-stone mb-8">This intake is no longer available or is fully booked.</p>
          <Link to="/programs">
            <Button className="btn-primary">View Other Programs</Button>
          </Link>
        </main>
      </div>
    );
  }

  // Bank transfer submitted view
  if (bankTransferSubmitted && bankDetails) {
    return (
      <div className="min-h-screen bg-off-white" data-testid="enrollment-page">
        <header className="bg-white border-b border-olive/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/programs" className="inline-flex items-center text-stone hover:text-olive transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Programs
            </Link>
            <Link to="/" className="font-heading text-xl italic text-charcoal">
              The Pack Standard
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium"
          >
            <div className="text-center mb-8">
              <Building2 className="w-16 h-16 text-olive mx-auto mb-4" />
              <h1 className="font-heading text-3xl text-charcoal mb-2">Bank Transfer Details</h1>
              <p className="text-stone">Complete your payment using the details below</p>
            </div>

            <div className="bg-sand/50 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Account Name</p>
                  <p className="font-medium text-charcoal">{bankDetails.account_name}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.account_name, 'name')}
                  className="text-olive"
                >
                  {copied === 'name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">BSB</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.bsb}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.bsb, 'bsb')}
                  className="text-olive"
                >
                  {copied === 'bsb' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Account Number</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.account_number}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.account_number, 'account')}
                  className="text-olive"
                >
                  {copied === 'account' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone">Amount</p>
                  <p className="font-heading text-2xl text-olive">${bankDetails.amount} AUD</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-olive/10">
                <div>
                  <p className="text-sm text-stone">Reference (Important!)</p>
                  <p className="font-medium text-charcoal font-mono">{bankDetails.reference}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(bankDetails.reference, 'reference')}
                  className="text-olive"
                >
                  {copied === 'reference' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-terracotta/10 rounded-lg">
              <p className="text-sm text-charcoal">
                <strong>Important:</strong> Please use the reference above so we can match your payment. 
                We&apos;ll confirm your enrollment once we receive the payment (usually within 1-2 business days).
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-stone text-sm mb-4">
                A copy of these details has been sent to your email.
              </p>
              <Link to="/dashboard">
                <Button className="btn-primary">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="enrollment-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/programs" className="inline-flex items-center text-stone hover:text-olive transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Programs
          </Link>
          <Link to="/" className="font-heading text-xl italic text-charcoal">
            The Pack Standard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= s ? "bg-olive text-white" : "bg-sand text-stone"
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 2 && (
                <div className={`w-24 h-1 mx-2 ${step > s ? "bg-olive" : "bg-sand"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Program Summary */}
        <div className="card-premium mb-8">
          <h2 className="font-heading text-2xl text-charcoal mb-4">{intake.program_name}</h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2 text-charcoal">
              <Calendar className="w-4 h-4 text-olive" />
              Starts {new Date(intake.start_date).toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long"
              })}
            </div>
            <div className="flex items-center gap-2 text-charcoal">
              <Clock className="w-4 h-4 text-olive" />
              {intake.day_of_week}s for 4 weeks
            </div>
            <div className="text-olive font-heading text-xl text-right">
              $440
            </div>
          </div>
        </div>

        {/* Step 1: Select Puppy & Referral */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl text-charcoal mb-4 text-center">
              Select Your Puppy
            </h1>
            <p className="text-stone text-center mb-8">
              Choose which puppy you&apos;d like to enroll in this program.
            </p>

            {eligiblePuppies.length === 0 ? (
              <div className="card-premium text-center py-12">
                <Dog className="w-16 h-16 text-olive/50 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Puppy Added</h3>
                <p className="text-stone mb-4">
                  You haven't added your puppy yet. Add your puppy to enroll in this program.
                </p>
                <Link to="/dashboard">
                  <Button className="btn-primary" data-testid="add-puppy-btn">
                    Add Your Puppy
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {eligiblePuppies.map((puppy) => {
                    const ageAtCourse = calculateAgeAtCourseStart(puppy);
                    return (
                      <motion.div
                        key={puppy.id}
                        whileHover={{ y: -4 }}
                        onClick={() => setSelectedPuppy(puppy)}
                        className={`card-premium cursor-pointer transition-all ${
                          selectedPuppy?.id === puppy.id ? "border-olive border-2" : ""
                        }`}
                        data-testid={`select-puppy-${puppy.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-full bg-olive/10 flex items-center justify-center">
                            <Dog className="w-8 h-8 text-olive" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-heading text-xl text-charcoal">{puppy.name}</h3>
                            <p className="text-stone text-sm">{puppy.breed}</p>
                            <p className="text-stone text-sm mt-1">
                              Currently {puppy.age_weeks} weeks old
                            </p>
                            {ageAtCourse !== null && (
                              <p className="text-olive text-sm font-medium mt-1">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {ageAtCourse} weeks at course start
                              </p>
                            )}
                          </div>
                          {selectedPuppy?.id === puppy.id && (
                            <Check className="w-6 h-6 text-olive" />
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Referral Source Section */}
                {selectedPuppy && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-premium mb-8"
                  >
                    <h3 className="font-heading text-xl text-charcoal mb-4">How did you hear about us?</h3>
                    <p className="text-stone text-sm mb-4">This helps us understand how families find The Pack Standard.</p>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Referral Source</Label>
                        <Select
                          value={referralSource}
                          onValueChange={setReferralSource}
                        >
                          <SelectTrigger data-testid="referral-source-select">
                            <SelectValue placeholder="Select how you found us" />
                          </SelectTrigger>
                          <SelectContent>
                            {REFERRAL_SOURCES.map(source => (
                              <SelectItem key={source.value} value={source.value}>
                                {source.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {(referralSource === "friend_family" || referralSource === "breeder" || referralSource === "other") && (
                        <div className="space-y-2">
                          <Label>
                            {referralSource === "friend_family" && "Who referred you? (optional)"}
                            {referralSource === "breeder" && "Breeder name (optional)"}
                            {referralSource === "other" && "Please specify (optional)"}
                          </Label>
                          <Input
                            value={referralDetails}
                            onChange={(e) => setReferralDetails(e.target.value)}
                            placeholder={
                              referralSource === "friend_family" ? "e.g., Sarah from our puppy group"
                              : referralSource === "breeder" ? "e.g., Happy Paws Breeding"
                              : "e.g., Local community board"
                            }
                            data-testid="referral-details-input"
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-center">
                  <Button
                    onClick={createEnrollment}
                    disabled={!selectedPuppy}
                    className="btn-primary"
                    data-testid="continue-to-payment"
                  >
                    Continue to Payment
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && enrollment && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl text-charcoal mb-4 text-center">
              Complete Payment
            </h1>
            <p className="text-stone text-center mb-8">
              Choose your preferred payment method
            </p>

            <div className="max-w-md mx-auto">
              <div className="card-premium mb-6">
                <h3 className="font-heading text-xl text-charcoal mb-4">Enrollment Summary</h3>
                <div className="space-y-3 text-sm border-b border-olive/10 pb-4 mb-4">
                  <div className="flex justify-between">
                    <span className="text-stone">Puppy</span>
                    <span className="text-charcoal">{enrollment.puppy_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone">Program</span>
                    <span className="text-charcoal">{enrollment.program_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone">Start Date</span>
                    <span className="text-charcoal">{enrollment.start_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone">Day</span>
                    <span className="text-charcoal">{enrollment.day_of_week}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone">Duration</span>
                    <span className="text-charcoal">4 weeks</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-charcoal font-medium">Total</span>
                  <span className="font-heading text-2xl text-olive">${enrollment.price} AUD</span>
                </div>
              </div>

              {processingPayment ? (
                <div className="card-premium text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-olive border-t-transparent mx-auto mb-4" />
                  <p className="text-stone">Processing...</p>
                </div>
              ) : !selectedMethod ? (
                <div className="card-premium space-y-3">
                  <h3 className="font-heading text-lg text-charcoal mb-4">Select Payment Method</h3>
                  
                  {/* Credit Card / Stripe */}
                  {paymentMethods?.methods?.includes('stripe') && (
                    <button
                      onClick={() => setSelectedMethod('stripe')}
                      className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                      data-testid="select-stripe"
                    >
                      <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-olive" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal">Credit / Debit Card</p>
                        <p className="text-sm text-stone">Pay securely with Stripe</p>
                      </div>
                    </button>
                  )}

                  {/* PayPal */}
                  {paypalClientId && (
                    <button
                      onClick={() => setSelectedMethod('paypal')}
                      className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                      data-testid="select-paypal"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#003087]/10 flex items-center justify-center">
                        <span className="font-bold text-[#003087] text-sm">PP</span>
                      </div>
                      <div>
                        <p className="font-medium text-charcoal">PayPal</p>
                        <p className="text-sm text-stone">Pay with your PayPal account</p>
                      </div>
                    </button>
                  )}

                  {/* Bank Transfer */}
                  {paymentMethods?.methods?.includes('bank_transfer') && paymentMethods?.bank_details?.available && (
                    <button
                      onClick={() => setSelectedMethod('bank_transfer')}
                      className="w-full p-4 border-2 border-olive/20 rounded-xl hover:border-olive transition-colors flex items-center gap-4 text-left"
                      data-testid="select-bank-transfer"
                    >
                      <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-olive" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal">Bank Transfer</p>
                        <p className="text-sm text-stone">Direct deposit to our account</p>
                      </div>
                    </button>
                  )}
                </div>
              ) : selectedMethod === 'stripe' ? (
                <div className="card-premium space-y-4">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedMethod(null)}
                    className="text-stone hover:text-charcoal"
                  >
                    ← Back to payment options
                  </Button>
                  
                  <div className="text-center py-4">
                    <CreditCard className="w-12 h-12 text-olive mx-auto mb-4" />
                    <p className="text-charcoal mb-4">You&apos;ll be redirected to Stripe&apos;s secure checkout page</p>
                    <Button
                      onClick={handleStripeCheckout}
                      className="btn-primary w-full"
                      data-testid="stripe-checkout-btn"
                    >
                      Pay with Card - ${enrollment.price} AUD
                    </Button>
                  </div>
                </div>
              ) : selectedMethod === 'paypal' ? (
                <div className="card-premium space-y-4">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedMethod(null)}
                    className="text-stone hover:text-charcoal"
                  >
                    ← Back to payment options
                  </Button>
                  
                  <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "AUD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "black", shape: "pill" }}
                      disabled={processingPayment}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [
                            {
                              description: `${enrollment.program_name} - ${enrollment.puppy_name}`,
                              amount: {
                                currency_code: "AUD",
                                value: enrollment.price.toString()
                              }
                            }
                          ]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const details = await actions.order.capture();
                        handlePaymentSuccess(details);
                      }}
                      onError={(err) => {
                        console.error("PayPal error:", err);
                        toast.error("Payment failed. Please try again.");
                      }}
                      data-testid="paypal-buttons"
                    />
                  </PayPalScriptProvider>
                </div>
              ) : selectedMethod === 'bank_transfer' ? (
                <div className="card-premium space-y-4">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedMethod(null)}
                    className="text-stone hover:text-charcoal"
                  >
                    ← Back to payment options
                  </Button>
                  
                  <div className="text-center py-4">
                    <Building2 className="w-12 h-12 text-olive mx-auto mb-4" />
                    <p className="text-charcoal mb-2">Bank Transfer</p>
                    <p className="text-sm text-stone mb-4">
                      We&apos;ll send you our bank details. Your spot will be confirmed once we receive your payment.
                    </p>
                    <Button
                      onClick={handleBankTransfer}
                      className="btn-primary w-full"
                      data-testid="bank-transfer-btn"
                    >
                      Get Bank Details
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="card-premium text-center p-6">
                  <AlertCircle className="w-12 h-12 text-terracotta mx-auto mb-3" />
                  <p className="text-charcoal font-medium">Payment Not Available</p>
                  <p className="text-sm text-stone mt-2">
                    No payment methods are configured. Please contact us to complete your enrollment.
                  </p>
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="btn-outline mt-4"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              )}

              <p className="text-center text-stone text-sm mt-4">
                Your spot will be reserved once payment is complete.
              </p>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default EnrollmentPage;
